import Vue from 'vue'
import { map, isNil, round } from 'lodash'
import moment from 'moment'

function NebNumber (number, numberType, rounded, defaultValue, conversionCount) {
  if (!number && number !== 0) {
    return defaultValue || '\u2014'
  }

  if (rounded) {
    number = Math.round(number)
  }

  switch (numberType) {
    case 'duration':
    case 'time':
      if (moment.duration(number, 's')._data.hours > 0) {
        return moment(moment.duration(number, 's')._data).format('hh:mm:ss')
      } else {
        return moment(moment.duration(number, 's')._data).format('mm:ss')
      }
    case /^conversion_index$/gmi.test(numberType) && numberType:
      if (number === -1 || number === null || number === undefined) {
        return '\u2014'
      }
      number = Math.round(number / 10) / 10
      if (conversionCount > 0 && number === 0) {
        return '< 0.1x'
      }

      return `${Intl.NumberFormat(undefined, {minimumFractionDigits: 1, maximumFractionDigits: 1}).format(number)}x`

    case 'completion':
    case 'percentage':
      return `${number}%`

    default:
      return Intl.NumberFormat().format(number)
  }
}

/** @description Formats a value for display according to the type specified.
 *    Typically used as a Vue filter, e.g.
 *
 *      <span>{{ data | nebFormat }}</span>
 *
 * @param {*} value The value to format
 * @param type {string} type of value, must be one of 'string', 'number', 'duration', 'percent', 'index'
 * @param defaultValue {string} default used if value is null or undefined
 *
 * @returns {string}
 */
function nebFormat (value, type = 'number', defaultValue = '\u2014') {
  if (value === null || value === undefined) {
    return defaultValue
  }

  switch (type) {
    case 'string':
      return value

    case 'number':
      return Intl.NumberFormat().format(value)

    case 'humanized-number':
      return humanizeNumber(value)

    case 'duration':
      return moment.utc(
        moment.duration(value, 's').asMilliseconds()
      ).format('mm:ss')

    case 'percent':
      if (value > 0 && value < 0.005) {
        return '< 1%'
      }

      return `${Math.round(value * 100)}%`

    case 'index':
      if (value < 0.05) {
        return '< 0.1x'
      }

      return `${Intl.NumberFormat(undefined, {minimumFractionDigits: 1, maximumFractionDigits: 1}).format(value)}x`

    default:
      return value
  }
}

/**
 * @description Format a number to be "Human-readable" by truncating and adding a suffix
 *   NOTE: numbers are never rounded up, we want 1,490 to be displayed as 1.4k, not 1.5k to avoid
 *         inflating stats
 *
 *   Suffixes:
 *     n - nano
 *     u - micro
 *     m - mili
 *     k - kilo
 *     M - million
 *     B - billion
 * @example
 * humanizeNumber(1234) // 1.2k
 * humanizeNumber(98765) // 98.7k
 *
 * @param  {Number} value The numeric value to format
 * @return {string}       Humanzied output
 */
function humanizeNumber (value) {
  function format (v) {
    return Intl.NumberFormat(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 1}).format(v)
  }

  const suffixes = {
    1e-9: 'n',
    1e-6: 'u',
    1e-3: 'm',
    1: '',
    1e3: 'k',
    1e6: 'M',
    1e9: 'B'
  }

  const suffixBreakpoints = [1e-9, 1e-6, 1e-3, 1, 1e3, 1e6, 1e9, 1e12, 1e15]
  if (value === undefined || value === null || value < 0) {
    value = 0
  }

  value = Math.floor(value)

  if (value === 0 || value === 1) {
    return format(value)
  }

  if (suffixes[value] !== undefined) {
    return `${format(1)}${suffixes[value]}`
  }

  let breakpoint
  for (let i = 0; i < suffixBreakpoints.length; i++) {
    if (suffixBreakpoints[i] > value) {
      breakpoint = suffixBreakpoints[i - 1]
      break
    }
  }

  const suffix = suffixes[breakpoint]
  if (suffix === undefined) {
    return value
  }

  const result = Math.floor(value / (breakpoint / 10)) / 10

  return `${format(result)}${suffix}`
}

Vue.filter('nebFormat', function (data, defaultValue) {
  const {value, type} = data
  return nebFormat(value, type, defaultValue)
})

Vue.filter('replace', function (value, substr, replacement) {
  if (!value) return ''
  const newValue = value.replace(new RegExp(substr, 'g'), replacement)
  return newValue
})

Vue.filter('capitalizeAll', function (value) {
  if (!value) return ''
  const stringParts = value.split(' ')

  const newValue = map(stringParts, function (item) {
    return item.charAt(0).toUpperCase() + item.slice(1)
  })
  return newValue.join(' ')
})

Vue.filter('defaultValue', function (value, replacement, includeZeroes = false) {
  if (value === undefined || value === null || (includeZeroes === true && (value === 0 || value === '0'))) {
    return replacement
  }
  return value
})

Vue.filter('toLocaleString', function (value, replacement) {
  if (value === undefined || value === null) {
    return replacement
  }

  return value.toLocaleString()
})

Vue.filter('secondsToHMS', function (v) {
  if (v < 3600) {
    return moment.utc(v * 1000).format('mm:ss')
  } else if (v < 86400) {
    return moment.utc(v * 1000).format('HH:mm:ss')
  } else {
    const days = Math.floor(v / 86400)
    v = moment.utc(v * 1000).format('HH:mm:ss')
    return `${days}d ${v}`
  }
})

Vue.filter('numberFormat', function (v, formatType = 'number', rounded = false, defaultValue = '', conversionCount = null) {
  return NebNumber(v, formatType, rounded, defaultValue, conversionCount)
})

Vue.filter('_round', (value, precision) => {
  if (isNil(value)) {
    return 0
  }
  return round(value, precision)
})

Vue.filter('truncate', (value, length) => {
  length = parseInt(length)

  if (value.length < length) {
    return value
  }

  const wordArray = value.split(/\s/g)

  const truncatedSentence = wordArray.reduce((acc, value) => {
    const newSentence = `${acc} ${value}`

    return newSentence.length > length ? acc : newSentence
  }, '')

  return `${truncatedSentence}...`
})

export { NebNumber }
